import { supabase, isSupabaseConfigured, getCurrentUserId } from "@/lib/supabase";
import type { GitHubIntegration, InsertGitHubIntegration } from "@shared/schema";

class GitHubIntegrationService {
  async getGitHubIntegration(): Promise<GitHubIntegration | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;

    try {
      const { data, error } = await supabase
        .from("github_integrations")
        .select("*")
        .eq("user_id", userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116' || error.code === '406') {
          // No rows returned or table not accessible
          return null;
        }
        return null;
      }

      return data ? this.mapToGitHubIntegration(data) : null;
    } catch {
      return null;
    }
  }

  async getConnectionStatus(): Promise<{ connected: boolean; username?: string; avatarUrl?: string }> {
    try {
      const integration = await this.getGitHubIntegration();
      if (!integration) {
        return { connected: false };
      }

      const isExpired = integration.expiresAt && new Date(integration.expiresAt) < new Date();

      return {
        connected: !isExpired,
        username: integration.githubUsername || undefined,
        avatarUrl: integration.githubAvatarUrl || undefined,
      };
    } catch {
      return { connected: false };
    }
  }

  async disconnect(): Promise<void> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const userId = await getCurrentUserId();
    if (!userId) throw new Error("Not authenticated");

    try {
      const { error } = await supabase
        .from("github_integrations")
        .delete()
        .eq("user_id", userId);

      if (error && error.code !== '406') {
        throw error;
      }
    } catch (e) {
      if (e instanceof Error && !e.message.includes('Service not configured')) {
        throw e;
      }
    }
  }

  private mapToGitHubIntegration(data: any): GitHubIntegration {
    return {
      id: data.id,
      userId: data.user_id,
      accessToken: data.access_token,
      tokenType: data.token_type,
      scope: data.scope,
      githubUserId: data.github_user_id,
      githubUsername: data.github_username,
      githubAvatarUrl: data.github_avatar_url,
      expiresAt: data.expires_at,
      refreshToken: data.refresh_token,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export const githubIntegrationService = new GitHubIntegrationService();
